import {
  addDays,
  differenceInDays,
  format,
  getISOWeeksInYear,
  parse,
  setISOWeek,
  startOfISOWeek,
  startOfISOWeekYear,
} from 'date-fns';
import { enUS, nb } from 'date-fns/locale';
import { Language, toLocaleTag } from './i18n/LanguageStore';

const toLocale = (locale: string) => {
  return locale === 'no' ? nb : enUS;
};

export const getStartWeekOfSemester = (semester: 1 | 2) =>
  semester === 1 ? 1 : 27;

export const getEndWeekOfSemester = (semester: 1 | 2, year: number) =>
  semester === 1 ? 26 : getISOWeeksInYear(new Date(year, 1, 1));

export class WeekYear {
  public readonly semester: 1 | 2;
  public readonly week: number;
  public readonly year: number;

  constructor(week: number, year: number) {
    this.semester = week <= getEndWeekOfSemester(1, year) ? 1 : 2;
    this.week = week;
    this.year = year;
  }

  static getWeeksArray(
    initialYear: number,
    initialSemester: 1 | 2,
    weeksOffset: number,
  ): WeekYear[] {
    const result: WeekYear[] = [];
    let weeksToSkip = weeksOffset;
    for (let semI = 0; semI < 3 + weeksOffset / 26; semI++) {
      const year =
        initialYear + Math.floor((1 - (initialSemester % 2) + semI) / 2);
      if (year === initialYear + 1 && semI === initialSemester) {
        break;
      }
      const semester = initialSemester % 2 === semI % 2 ? 2 : 1;
      const firstWeekOfSemester = getStartWeekOfSemester(semester);
      const lastWeekOfSemester = getEndWeekOfSemester(semester, year);
      for (
        let weekI = firstWeekOfSemester;
        weekI <= lastWeekOfSemester;
        weekI++
      ) {
        if (--weeksToSkip < 0) {
          result.push(new WeekYear(weekI, year));
        }
      }
    }
    return result;
  }
}

export const getStartOfWeek = (weekYear: WeekYear) => {
  const startOfIsoWeekYear = startOfISOWeekYear(new Date(weekYear.year, 6, 6));
  const setIsoWeek = setISOWeek(startOfIsoWeekYear, weekYear.week);

  return startOfISOWeek(setIsoWeek);
};

export const getWeekDay = (weekYear: WeekYear, daysOffset: number) => {
  return addDays(getStartOfWeek(weekYear), daysOffset);
};

// Returns the ISO week of the date.
export const getWeek = function (fromDate: Date) {
  const date = new Date(fromDate.getTime());
  date.setHours(0, 0, 0, 0);
  // Thursday in current week decides the year.
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  // January 4 is always in week 1.
  const week1 = new Date(date.getFullYear(), 0, 4);
  // Adjust to Thursday in week 1 and count number of weeks from date to week1.
  return (
    1 +
    Math.round(
      ((date.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getDay() + 6) % 7)) /
        7,
    )
  );
};

export const daysDifference = (
  dateFromIsoInclusive: string,
  dateToIsoInclusive: string,
) => {
  return (
    differenceInDays(
      parse(dateToIsoInclusive, 'yyyy-MM-dd', new Date()),
      parse(dateFromIsoInclusive, 'yyyy-MM-dd', new Date()),
    ) + 1
  );
};

export const toStartOfDay = (date: Date): Date =>
  new Date(date.setHours(0, 0, 0, 0));

export const formatIsoTzLess = (date: Date) => {
  return format(date, 'yyyy-MM-dd');
};

export const todayAsFullDate = (language: Language) => {
  return format(Date.now(), 'PPP', { locale: toLocale(toLocaleTag(language)) });
};

export const dateWithLocale = (date: Date, language: Language) => {
  return format(date, 'PP', { locale: toLocale(toLocaleTag(language)) });
};

export const fullDateWithLocale = (date: Date, language: Language) => {
  return format(date, 'PPP', { locale: toLocale(toLocaleTag(language)) });
};

export const dateTimeNorwegianLocale = (date: Date) =>
  format(date, 'dd.MM.yyyy - HH:mm:ss');

export const shortDateWithLocale = (date: Date, language: Language) => {
  const formatted = fullDateWithLocale(date, language);
  // Examples: Norwegian: "30. juni 2020", English: "June 30th, 2020"
  // Remove year preceded by optional comma and space:
  return formatted.replace(/,?\s*\d{4}$/, '');
};

export const parseIsoTzLess = (dateIso: string) => {
  return parse(dateIso, 'yyyy-MM-dd', new Date());
};

export const formatLocaleNo = (date: Date | string) =>
  format(new Date(date), 'dd.MM.yyyy');

export const getStudyYearForDate = (date: Date): [Date, Date] => {
  // Compute the date range of the current year of study.
  // The default FS semester 1 starts July 1 and semester 2 starts January 1.
  const fromYear =
    date.getMonth() <= 5 ? date.getFullYear() - 1 : date.getFullYear();
  const toYear = fromYear + 1;
  const fromDate = new Date(fromYear, 6, 1);
  const toDate = new Date(new Date(toYear, 6, 1).getTime() - 1);

  return [fromDate, toDate];
};

export const sortDates = (
  dateStringA: string | undefined,
  dateStringB: string | undefined,
): 0 | 1 | -1 => {
  if (!dateStringA) return -1;
  if (!dateStringB) return 1;

  const dateA = new Date(dateStringA);
  const dateB = new Date(dateStringB);

  if (dateA === dateB) return 0;

  return dateA < dateB ? 1 : -1;
};
